/**
 * Typed API client for Inbound Webhooks (epic 202).
 *
 * Tenant admins mint bearer tokens that external systems use to POST inbound
 * events at `/webhooks/in/{token}/{action}`. The full token is shown once at
 * creation (reveal-once banner); afterwards only the prefix is visible. The
 * available actions + their required scopes are published at a root-mounted
 * metadata endpoint.
 */

import { apiClient } from '@/lib/apiClient';
import { rootClient } from '@/lib/rootClient';

export interface InboundTokenRow {
  id: string;
  name: string;
  tokenPrefix: string;
  allowedScopes: string[];
  expiresAt: string | null;
  lastUsedAt: string | null;
  useCount: number;
  enabled: boolean;
  revoked: boolean;
  createdAt: string;
}

export interface CreateTokenRequest {
  name: string;
  allowedScopes: string[];
  expiresAt?: string | null;
}

/** `token` is the full bearer token — shown once at creation. */
export interface CreatedInboundToken {
  id: string;
  token: string;
  tokenPrefix: string;
}

export interface ActionMeta {
  name: string;
  description: string;
  requiredScope: string;
}

export async function listInboundTokens(): Promise<InboundTokenRow[]> {
  const { data } = await apiClient.get<InboundTokenRow[]>('/admin/inbound-webhooks');
  return data;
}

export async function createInboundToken(
  req: CreateTokenRequest,
): Promise<CreatedInboundToken> {
  const { data } = await apiClient.post<CreatedInboundToken>('/admin/inbound-webhooks', req);
  return data;
}

export async function deleteInboundToken(id: string): Promise<void> {
  await apiClient.delete(`/admin/inbound-webhooks/${id}`);
}

/** Root-mounted: the inbound actions catalogue + their required scopes. */
export async function listInboundActions(): Promise<ActionMeta[]> {
  const { data } = await rootClient.get<ActionMeta[]>('/webhooks/in/_meta/actions');
  return data;
}
